import type { AlertVisibility } from '@kirin/api-client/contracts/alerts';

/**
 * The set of valid visibility values. Mirrors the shared
 * `alertVisibilitySchema` enum on the wire so the front-end and back-end
 * cannot drift.
 */
const VALID_VISIBILITIES: readonly AlertVisibility[] = [
  'active',
  'all',
  'suppressed',
];

/**
 * Build the localStorage key that stores the alerts-visibility selector
 * state for a given alerts table scope. KIR-2512 supersedes the two legacy
 * keys (`showMutedAlerts_${scopeKey}`, `showExcludedAlerts_${scopeKey}`),
 * which are still consulted once for migration purposes (see
 * `readAlertVisibilityFromStorage`).
 *
 * Org alerts use `org:<organizationId>` so each org has its own preference;
 * account/dashboard alerts use the literal string `'account'`.
 */
export function alertVisibilityStorageKey(scopeKey: string): string {
  return `alertVisibility_${scopeKey}`;
}

/**
 * URL param that seeds the visibility tab on fresh navigation
 * (KIR-2606). Ephemeral persistence vector — stripped by
 * `useAlertVisibility` after first read; storage helpers above are the
 * durable counterpart.
 */
export const ALERTS_VISIBILITY_URL_PARAM = 'visibility';

function legacyMutedKey(scopeKey: string): string {
  return `showMutedAlerts_${scopeKey}`;
}

function legacyExcludedKey(scopeKey: string): string {
  return `showExcludedAlerts_${scopeKey}`;
}

function isAlertVisibility(value: unknown): value is AlertVisibility {
  return (
    typeof value === 'string' &&
    (VALID_VISIBILITIES as readonly string[]).includes(value)
  );
}

/**
 * Read the persisted visibility selector value for `scopeKey`. Falls back
 * to the legacy two-toggle keys (KIR-2363 / KIR-842) on first read so users
 * who previously turned ON either suppression toggle land on `'all'`
 * instead of being silently downgraded to `'active'`. Otherwise defaults
 * to `'active'` per the KIR-2512 spec.
 *
 * Safe to call during render — never throws (`localStorage` may be
 * unavailable in SSR / privacy modes).
 */
export function readAlertVisibilityFromStorage(
  scopeKey: string
): AlertVisibility {
  try {
    if (typeof window === 'undefined') return 'active';

    const raw = window.localStorage.getItem(
      alertVisibilityStorageKey(scopeKey)
    );
    if (isAlertVisibility(raw)) {
      return raw;
    }

    // Legacy migration — only consulted when the new key is absent or
    // unparseable. We intentionally don't write the migrated value back
    // here: doing so during render would be an unexpected side effect, and
    // the selector persists on first user interaction anyway, which is
    // sufficient to "lock in" the migrated default for future loads.
    const legacyMuted =
      window.localStorage.getItem(legacyMutedKey(scopeKey)) === 'true';
    const legacyExcluded =
      window.localStorage.getItem(legacyExcludedKey(scopeKey)) === 'true';
    if (legacyMuted || legacyExcluded) {
      return 'all';
    }

    return 'active';
  } catch {
    return 'active';
  }
}

/**
 * Write the visibility selector value for `scopeKey`. Swallows storage
 * errors (privacy mode / quota) — the selector still works in-memory.
 */
export function writeAlertVisibilityToStorage(
  scopeKey: string,
  value: AlertVisibility
): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(alertVisibilityStorageKey(scopeKey), value);
  } catch {
    // Privacy/quota errors are non-fatal; the selector still works in-memory.
  }
}
